import { authSession } from 'src/auth/auth-session';
import { ErrorMessage } from 'src/shared/enum/ErrorMessage';

import { BasketUser } from '../basketService';
import { studentBookingClient } from '../client';
import { PaymentPlanDto } from '../paymentPlanService/dto/PaymentPlanDto';
import { PaymentOptionDto } from '../paymentPlanService/dto/PaymentOptionDto';
import { StudentBookingSuccessResponse } from '../types';

import { AmendServiceEndpoints } from './constants';
import { AmendBookingData, SelfServeAmendDetails } from './types';
import { ProcessAmendDto } from './dto/ProcessAmend.dto';
import { ProcessSelfServeAmendDto } from './dto/ProcessSelfServeAmend.dto';

export const getAmendData = async (vitaRef: string): Promise<AmendBookingData> => {
    const token = await authSession();
    if (!token) throw new Error(ErrorMessage.AUTH_ID_ERROR);

    const { data: response } = await studentBookingClient.get(
        `${AmendServiceEndpoints.GetAmendData}/${vitaRef}`,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );
    const { data } = response;

    return {
        ...data,
        payment_plan: new PaymentPlanDto({
            weeklyPrice: data.payment_plan.weekly_price,
            discountApplied: data.payment_plan.discount_applied,
            contractLengthRatio: data.payment_plan.contract_length_ratio,
            paymentPlanPremiumRatio: data.payment_plan.payment_plan_premium_ratio,
            paymentOptions: data.payment_plan.payment_options.map(
                (option: {
                    index: number;
                    name: string;
                    due_date: string;
                    from_date: string;
                    to_date: string;
                    weeks: number;
                    price: number;
                }) =>
                    new PaymentOptionDto({
                        index: option.index,
                        name: option.name,
                        dueDate: option.due_date,
                        fromDate: option.from_date,
                        toDate: option.to_date,
                        weeks: option.weeks,
                        price: option.price,
                    })
            ),
            promotion: data.payment_plan.promotion,
            subtotal: data.payment_plan.subtotal,
            totalRent: data.payment_plan.total_rent,
            genericPrice: data.payment_plan.generic_price,
        }),
    };
};

export const postAmendNoAdminFee = async (payload: ProcessAmendDto): Promise<void> => {
    const token = await authSession();
    if (!token) throw new Error(ErrorMessage.AUTH_ID_ERROR);

    await studentBookingClient.post(AmendServiceEndpoints.PostAmendNoAdminFee, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

export const postAmend = async (payload: ProcessAmendDto): Promise<void> => {
    const token = await authSession();
    if (!token) throw new Error(ErrorMessage.AUTH_ID_ERROR);

    await studentBookingClient.post(AmendServiceEndpoints.PostAmend, payload, {
        headers: {
            Authorization: `Bearer ${token}`,
        },
    });
};

export const postAmendUpdateOccupantDetails = async (details: {
    vitaRef: string;
    occupant: BasketUser;
}) => {
    const token = await authSession();
    if (!token) throw new Error(ErrorMessage.AUTH_ID_ERROR);

    const { data: response } = await studentBookingClient.post<StudentBookingSuccessResponse<null>>(
        AmendServiceEndpoints.PostUpdateOccupantDetails,
        details,
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );

    return response.data;
};

export const getSelfServeAmendDetails = async (
    bookingId: string
): Promise<SelfServeAmendDetails> => {
    const { data: response } = await studentBookingClient.get(
        `${AmendServiceEndpoints.SelfServeAmend}/${bookingId}`
    );
    const { data } = response;

    return data;
};

export const postSelfServeAmend = async (
    payload: ProcessSelfServeAmendDto,
    bookingId: string
): Promise<string> => {
    const { data: response } = await studentBookingClient.post(
        `${AmendServiceEndpoints.SelfServeAmend}/${bookingId}`,
        payload
    );

    return response.data;
};
